import { getCurrentLocaleCode, trans } from "@mongez/localization";
import { productAtom } from "core/catalog";
import { userAtom } from "core/user";
import { useToggleWishlist, wishlistAtom } from "core/wishlist";
import { useEffect, useRef, useState } from "react";
import { FreeMode, Navigation, Thumbs } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  ArrowLeft,
  ArrowRight,
  errorToast,
  Heart,
  HeartFilled,
  Share,
  successToast,
} from "~/design-system/components";

import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";

export function ProductDetailsImages() {
  const localeCode = getCurrentLocaleCode();
  const product = productAtom.use("product");
  const user = userAtom.useValue();
  const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const [direction, setDirection] = useState<"horizontal" | "vertical">(
    "horizontal",
  );

  const { inWishlist, toggleWishlist } = useToggleWishlist(product.id, {
    onSubmit: (response) => {
      wishlistAtom.change("wishlist", response.wishlist);
      userAtom.update(response.user);
      successToast(trans("addToWishlist"));
    },
    onError: (error) => {
      errorToast(error);
    },
  });

  const handleShareButton = () => {
    navigator.share({ url: window.location.href });
  };

  useEffect(() => {
    const checkDirection = () => {
      if (window.innerWidth >= 1024) {
        setDirection("vertical");
      } else {
        setDirection("horizontal");
      }
    };

    checkDirection();
    window.addEventListener("resize", checkDirection);

    return () => {
      window.removeEventListener("resize", checkDirection);
    };
  }, []);

  return (
    <div className="mx-auto flex w-full flex-col-reverse gap-5 lg:h-[600px] lg:flex-row">
      <div className="h-[100px] w-full shrink-0 lg:h-full lg:w-[120px]">
        <Swiper
          onSwiper={setThumbsSwiper}
          spaceBetween={10}
          slidesPerView={4}
          freeMode={true}
          watchSlidesProgress={true}
          modules={[FreeMode, Navigation, Thumbs]}
          direction={direction}
          key={direction + localeCode}
          className="h-full w-full"
        >
          {product.images?.map((image, index) => (
            <SwiperSlide
              key={index}
              className="box-border cursor-pointer overflow-hidden rounded-2xl border-2 border-transparent transition-all [&.swiper-slide-thumb-active]:border-[#DF485F]"
            >
              <img
                src={image.large.url}
                alt={product.name}
                className="h-full w-full object-cover object-center"
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      <div className="relative h-fit w-full flex-1 overflow-hidden rounded-3xl border border-black bg-white max-sm:h-[400px] sm:h-[500px] lg:h-full">
        <Swiper
          key={localeCode}
          dir={localeCode === "ar" ? "rtl" : "ltr"}
          slidesPerView={1}
          spaceBetween={10}
          loop
          thumbs={{
            swiper:
              thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
          }}
          modules={[FreeMode, Navigation, Thumbs]}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          onBeforeInit={(swiper) => {
            if (
              typeof swiper.params.navigation !== "boolean" &&
              swiper.params.navigation
            ) {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
            }
          }}
          className="product-details-images-slider h-full!"
        >
          {product.images?.map((image, index) => (
            <SwiperSlide key={index} className="h-full! w-full">
              <img
                src={image.large.url}
                alt={product.name}
                className="h-full w-full object-contain object-center"
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Navigation Buttons */}
        <div className="pointer-events-none absolute top-1/2 left-0 z-30 flex w-full -translate-y-1/2 justify-between px-4">
          <button
            ref={prevRef}
            className="border-primary-default text-primary-default hover:bg-primary-default pointer-events-auto flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border bg-white/80 shadow-md transition-all hover:text-white disabled:opacity-0"
          >
            <ArrowLeft />
          </button>
          <button
            ref={nextRef}
            className="border-primary-default text-primary-default hover:bg-primary-default pointer-events-auto flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border bg-white/80 shadow-md transition-all hover:text-white disabled:opacity-0"
          >
            <ArrowRight />
          </button>
        </div>
        {product.badge?.label && (
          <span className="absolute top-5 left-5 z-30 rounded-full border border-black bg-[#FFE6EA] px-6 py-1.5 text-sm font-semibold text-[#DF485F] sm:text-base lg:text-xl">
            {product.badge.label}
          </span>
        )}
        <div className="absolute top-5 right-5 z-30 flex items-center gap-3">
          <button
            aria-label={trans("share")}
            title={trans("share")}
            type="button"
            onClick={handleShareButton}
            className="hover:bg-primary-500 flex size-14 cursor-pointer items-center justify-center rounded-full border bg-[#F6F6F6] transition-colors duration-300 hover:text-white"
          >
            <Share />
          </button>
          {user.type === "user" && (
            <button
              title={
                inWishlist
                  ? trans("removeFromWishlist")
                  : trans("addToWishlist")
              }
              aria-label={
                inWishlist
                  ? trans("removeFromWishlist")
                  : trans("addToWishlist")
              }
              type="button"
              onClick={toggleWishlist}
              className="hover:bg-primary-500 flex size-14 cursor-pointer items-center justify-center rounded-full border bg-[#F6F6F6] transition-colors duration-300 hover:text-white"
            >
              {inWishlist ? <HeartFilled /> : <Heart />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
